import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { TimeoutEnum } from 'app/core/enum/timeout.enum';
import { Categories } from 'app/shared/interfaces/upload-file-support-page';
import { SupportPageService } from 'app/core/services/support-page.service';
import { ToastrService } from 'ngx-toastr';
import { AlertService } from 'app/shared/components/alert/alert.service';

export const FILE_CATEGORIES_DOC_ID = 'file-categories';

const DEFAULT_CATEGORIES: Categories = {
  id: FILE_CATEGORIES_DOC_ID,
  categories: {
    softwares: {
      ptBR: 'Softwares',
      en: 'Software',
      es: 'Softwares',
      it: 'Software',
    },
    guides: { ptBR: 'Guias', en: 'Guides', es: 'Guías', it: 'Guide' },
    manuals: { ptBR: 'Manuais', en: 'Manuals', es: 'Manuales', it: 'Manuali' },
    others: { ptBR: 'Outros', en: 'Others', es: 'Otros', it: 'Altri' },
  },
};

@Component({
  standalone: false,
  selector: 'app-file-category-management',
  templateUrl: './file-category-management.component.html',
  styleUrls: ['./file-category-management.component.scss'],
})
export class FileCategoryManagementComponent implements OnInit {
  uploadFormFile!: FormGroup;
  loadError = false;

  languages = [
    { code: 'ptBR', label: 'Português', flag: 'PT.png' },
    { code: 'en', label: 'Inglês', flag: 'EN.png' },
    { code: 'es', label: 'Espanhol', flag: 'ES.png' },
    { code: 'it', label: 'Italiano', flag: 'IT.png' },
  ];

  private readonly fb = inject(FormBuilder);
  private readonly supportPageService = inject(SupportPageService);
  private readonly translateService = inject(TranslateService);
  private readonly dialog = inject(MatDialog);
  private readonly toastr = inject(ToastrService);
  private readonly alertService = inject(AlertService);

  ngOnInit(): void {
    this.uploadFormFile = this.fb.group({ rows: this.fb.array([]) });

    this.supportPageService
      .getSupportFileById(FILE_CATEGORIES_DOC_ID)
      .subscribe({
        next: (data: Categories) => {
          const hasData =
            data && data.categories && Object.keys(data.categories).length > 0;
          this.patchFormFromJson(hasData ? data : DEFAULT_CATEGORIES);
        },
        error: () => {
          this.loadError = true;
          this.patchFormFromJson(DEFAULT_CATEGORIES);
          this.translateService
            .get('global.support-error-load-categories')
            .subscribe((msg) => this.alertService.warning(msg));
        },
      });
  }

  get rows(): FormArray {
    return this.uploadFormFile.get('rows') as FormArray;
  }

  patchFormFromJson(json: Categories): void {
    const rowsArray = this.rows;
    rowsArray.clear();

    Object.entries(json.categories).forEach(([key, values]: [string, any]) => {
      const row = this.createRow(key, values);
      rowsArray.push(row);
    });
  }

  private createRow(key = '', values: any = {}): FormGroup {
    const row = this.fb.group({
      key: [key],
      ptBR: [values.ptBR || '', Validators.required],
      en: [values.en || '', Validators.required],
      es: [values.es || '', Validators.required],
      it: [values.it || '', Validators.required],
    });

    row.get('en')?.valueChanges.subscribe((value: string) => {
      const currentKey = row.get('key')?.value;
      if (value && (!currentKey || currentKey.trim() === '')) {
        row.get('key')?.setValue(this.toSnakeCase(value), { emitEvent: false });
      }
    });

    return row;
  }

  addRow(): void {
    this.rows.push(this.createRow());
  }

  removeRow(index: number): void {
    this.rows.removeAt(index);
  }

  toSnakeCase(input: string): string {
    return input.trim().toLowerCase().replace(/\s+/g, '_');
  }

  closeDialog(): void {
    this.dialog.closeAll();
  }

  onSubmit(): void {
    const categories: Record<string, any> = {};
    (this.uploadFormFile.value.rows as any[]).forEach((row) => {
      if (row.key?.trim()) {
        categories[row.key.trim()] = {
          ptBR: row.ptBR,
          en: row.en,
          es: row.es,
          it: row.it,
        };
      }
    });

    const document: Categories = { id: FILE_CATEGORIES_DOC_ID, categories };

    this.supportPageService.postSupportFile(document).subscribe({
      next: () => {
        this.translateService
          .get('global.alert.send-file-upload-success')
          .subscribe((msg) =>
            this.toastr.success(msg, '', {
              positionClass: 'toast-top-full-width',
              disableTimeOut: false,
            }),
          );
        setTimeout(() => this.dialog.closeAll(), TimeoutEnum.Medium);
      },
      error: () => {
        this.translateService
          .get('global.support-error-save-file-in-db')
          .subscribe((msg) =>
            this.toastr.error(msg, '', {
              positionClass: 'toast-top-full-width',
              disableTimeOut: false,
            }),
          );
      },
    });
  }
}
